import styled, { css } from 'styled-components';
import spacing from '../../styles/spacing';
import { inputEase, fontSize, TTextFieldSize } from './variables';
import { customFont } from '../../styles/fonts';
import { scrollThumb, scrollVertical } from '../../styles/mixins';
import { darken } from '../../utils/color.utils';
import { ICustomThemeInput, ITextField } from './HBTextField.types';
import { vars } from '../../theme';

/* Wrapper for standard input and textarea */
export const Fieldset = styled.div`
    position: relative;
    margin-bottom: ${spacing[20]};
`;

/* Placeholder util */
export function placeholder(_fontSize: { fontSize: string }, color: string) {
    return css`
        &::placeholder {
            color: ${color};
            transition: color ${inputEase};
            opacity: 0.6;
        }
    `;
}

function getCustomThemeHoverBorderColor(
    borderColor: string,
    background: string,
    errorColor: string,
    error: boolean
) {
    if (error) {
        return `border-color: ${errorColor};`;
    }

    // For cases where there's no border + an invisible background (which is explicitly defined)
    if (!borderColor && background === 'transparent') {
        return null;
    }

    if (!borderColor || borderColor === 'transparent') {
        return `border-color: ${darken(background, 0.1)}`;
    }

    return `border-color: ${darken(borderColor, 0.1)};`;
}

function getCustomThemeFocusBorderColor(
    borderColor: string,
    background: string,
    errorColor: string,
    error: boolean
) {
    if (error) {
        return `border-color: ${errorColor};`;
    }

    // fill only
    if (!borderColor || borderColor === 'transparent') {
        return `border-color: transparent`;
    }

    // border only
    if (background && background !== 'transparent') {
        return `border-color: ${darken(borderColor, 0.4)};`;
    }

    // border and fill
    return `border-color: ${darken(borderColor, 0.1)};`;
}

function getCustomThemeFocusBackgroundColor(background) {
    if (background === 'transparent') {
        return null;
    }

    return `background: ${
        background ? darken(background, 0.1) : vars.colors.surface.item
    };`;
}

/* Theme styles */
export function getCustomTheme(
    customTheme: ICustomThemeInput,
    error: boolean,
    size: TTextFieldSize
) {
    const {
        fontFamily,
        fontColor = vars.colors.text.primary,
        background,
        borderColor,
        borderRadius = null,
        padding = null,
        fontWeight,
        errorColor = vars.colors.base.danger
    } = customTheme;

    return css`
        color: ${fontColor};
        font-family: ${customFont[fontFamily]?.font_family ||
        fontFamily ||
        `inherit`};
        background: ${background || vars.colors.surface.item};
        font-weight: ${fontWeight};
        border: 1px solid ${borderColor || 'transparent'};
        ${borderRadius && `border-radius: ${borderRadius};`}
        ${padding && `padding: ${padding};`}
        font-size: ${fontSize[size].fontSize};

        ${error &&
        css`
            border-color: ${errorColor};
        `}

        &:hover {
            ${getCustomThemeHoverBorderColor(
                borderColor,
                background,
                errorColor,
                error
            )}
        }

        &:focus,
        &:focus-within {
            ${getCustomThemeFocusBackgroundColor(background)}
            ${getCustomThemeFocusBorderColor(
                borderColor,
                background,
                errorColor,
                error
            )}
        }
    `;
}

// These styles are defined here:
// zpl://screen?pid=5ef37a91707c097fa62c4c3b&sid=5f0721658adfb0784bc8d376
function getThemeStyles({
    size,
    customTheme,
    error,
    $showBorderWhenRequired: showBorder
}: ITextField) {
    if (customTheme) {
        return css`
            ${getCustomTheme(customTheme, error, size)}
            ${placeholder(fontSize[size], customTheme.fontColor)};
        `;
    }

    return css`
        color: ${vars.colors.text.primary};
        &::placeholder {
            color: ${vars.colors.text.tertiary};
        }

        background: ${vars.colors.surface.itemInset};
        border: ${showBorder
            ? `1px solid ${vars.colors.stroke.primary}`
            : '1px solid transparent'};
        &:focus,
        &:focus-within {
            background: ${vars.colors.surface.itemInsetHover};
            border-color: ${showBorder
                ? vars.colors.stroke.primary
                : 'transparent'};
        }

        ${error &&
        css`
            &,
            &:hover,
            &:focus,
            &:hover {
                border-color: ${vars.colors.base.danger};
            }
        `}

        &:disabled,
        &:disabled:hover {
            cursor: not-allowed;
            background: ${vars.colors.surface.itemInset};
            color: ${vars.colors.text.tertiary};
            border-color: transparent;
        }
    `;
}

/* Common styles */
const foundationResets = `
    height: unset;
    background: unset;

    &:focus {
        background: unset;
        border-color: transparent;
        outline: unset;
    }
`;

export const textField = css`
    font-family: ${vars.fonts.body};
    box-sizing: border-box;
    border: none;
    box-shadow: none;
    display: block;
    margin: 0;
    width: 100%;
    outline: none;
    border-radius: ${vars.radius.xs};
`;

export function textFieldBaseStyles(props: ITextField) {
    return css`
        ${foundationResets}
        ${textField}
        box-sizing: border-box;
        border: none;
        display: block;
        margin: 0;
        width: ${props.width || '100%'};
        outline: none;
        ${fontSize[props.size]};
        transition: background ${inputEase}, border-color ${inputEase};

        ${getThemeStyles(props)}

        /* zpl://screen?pid=5ef37a91707c097fa62c4c3b&sid=5f072153a192d76b26e49e26 */
        padding-right: 20px;
    `;
}

export const textAreaRoot = css`
    resize: none;
    overflow-y: scroll;

    &::-webkit-scrollbar {
        ${scrollVertical};
    }

    &::-webkit-scrollbar-thumb {
        ${scrollThumb};
    }
`;

// ─── DS Shared Field Primitives ──────────────────────────────────────────────
// Shared by HBInput, HBTextArea, and HBSelect DS modes (isNewHBMode).

export type HBFieldStateVariant = 'outlined' | 'ghost';

/**
 * Returns border and background CSS for a DS field container based on its
 * current interaction state. Each DS component's container calls this mixin
 * and may apply additional component-specific overrides on top.
 *
 * Disabled outlined: `stroke.disabled` border by default.
 */
export function hbFieldStateBorder({
    $variant = 'outlined',
    $error,
    $disabled,
    $focused,
    $disabledBorderColor = vars.colors.stroke.disabled
}: {
    $variant?: HBFieldStateVariant;
    $error?: boolean;
    $disabled?: boolean;
    $focused?: boolean;
    $disabledBorderColor?: string;
}) {
    if ($disabled) {
        return css`
            background: repeating-linear-gradient(
                -45deg,
                ${vars.colors.stroke.disabled},
                ${vars.colors.stroke.disabled} 1px,
                ${vars.colors.surface.item} 1px,
                ${vars.colors.surface.item} 7px
            );
            border: ${$variant === 'ghost'
                ? 'none'
                : `1px solid ${$disabledBorderColor}`};
            pointer-events: none;
            cursor: not-allowed;
        `;
    }
    if ($error) {
        return css`
            border: 1px solid ${vars.colors.stroke.danger};
        `;
    }
    if ($focused) {
        return css`
            border: 1px solid ${vars.colors.stroke.focus};
            box-shadow: inset 0 0 0 1px ${vars.colors.stroke.focus};
        `;
    }
    if ($variant === 'ghost') {
        return css`
            border: 1px solid transparent;
            background: transparent;
            &:hover {
                border-color: ${vars.colors.stroke.secondary};
            }
        `;
    }
    return css`
        border: 1px solid ${vars.colors.stroke.primary};
        &:hover {
            border-color: ${vars.colors.stroke.selected};
        }
    `;
}

/** Flex row for tag chips rendered inside a DS field. */
export const HBTagsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[4]};
`;

/**
 * Applies container-level customTheme overrides for a DS field FieldContainer.
 * Only applies borderColor in the rest state (not error/focused/disabled) so DS
 * interaction tokens are never overridden. Call this after hbFieldStateBorder so
 * the rest-state borderColor can take precedence over stroke.primary.
 */
export function getCustomThemeDSContainer(
    customTheme: ICustomThemeInput,
    $error?: boolean,
    $disabled?: boolean,
    $focused?: boolean
) {
    const { background, borderColor, borderRadius } = customTheme;
    const isRestState = !$error && !$disabled && !$focused;
    return css`
        ${!$disabled && background ? `background: ${background};` : ''}
        ${isRestState && borderColor ? `border-color: ${borderColor};` : ''}
        ${borderRadius ? `border-radius: ${borderRadius};` : ''}
    `;
}

/**
 * Applies text/font customTheme overrides for a DS field's inner input.
 * Uses `&&` specificity to win over the base `&&` color rule while still
 * losing to `&&:disabled` which has higher specificity — the disabled color
 * is never overridden.
 */
export function getCustomThemeDSInput(customTheme: ICustomThemeInput) {
    const { fontColor, fontFamily, fontWeight } = customTheme;
    if (!fontColor && !fontFamily && !fontWeight) {
        return null;
    }
    return css`
        && {
            ${fontColor ? `color: ${fontColor};` : ''}
            ${fontFamily
                ? `font-family: ${
                      customFont[fontFamily]?.font_family || fontFamily
                  };`
                : ''}
            ${fontWeight ? `font-weight: ${fontWeight};` : ''}
        }
        ${fontColor
            ? css`
                  &::placeholder {
                      color: ${fontColor};
                  }
              `
            : ''}
    `;
}
